/**
 * Heat Map utility functions ported from legacy web-controller
 * (ProductStore._calculateHeatMapColorScales, _assignColorsToProducts,
 *  DataVisualizationModal.translatePredefinedOptions).
 *
 * Pure functions only — no DOM, no Redux, no side effects.
 */

import moment from "moment";
import { HEAT_MAP_SPECTRUM_COLORS } from "../styles/themes/overrides/palette";

/**
 * The 8-color ColorBrewer Spectral diverging palette used by legacy heat map.
 * Index 0 = highest intensity (high-value end), index 7 = lowest.
 *
 * Source: legacy web-controller ProductStore.js HEAT_MAP_SPECTRUM
 */
export const HEAT_MAP_SPECTRUM: string[] = HEAT_MAP_SPECTRUM_COLORS;

export const HEAT_MAP_GRADIENT_SIZE = 8;

/**
 * Default high (highest value) and low (lowest value) colors — matches legacy
 * defaults: _heatMapStartColor = HEAT_MAP_SPECTRUM[0], _heatMapEndColor = HEAT_MAP_SPECTRUM[4].
 */
export const DEFAULT_HIGH_COLOR = HEAT_MAP_SPECTRUM[0];
export const DEFAULT_LOW_COLOR = HEAT_MAP_SPECTRUM[4];

/** Date range option keys — aligned with sorting rules periodTypes format. */
export const HEAT_MAP_DATE_RANGE_OPTIONS = {
  LAST_7_DAYS: "last_7_days",
  LAST_14_DAYS: "last_14_days",
  LAST_30_DAYS: "last_30_days",
  LAST_90_DAYS: "last_90_days",
  LAST_180_DAYS: "last_180_days",
  LAST_365_DAYS: "last_365_days",
  SPECIFIC_DATE: "specific_date",
  DATE_RANGE: "date_range",
} as const;

export type HeatMapDateRangeOption =
  (typeof HEAT_MAP_DATE_RANGE_OPTIONS)[keyof typeof HEAT_MAP_DATE_RANGE_OPTIONS];

/** Default selection: Last 7 Days. */
export const DEFAULT_DATE_RANGE_OPTION: HeatMapDateRangeOption =
  HEAT_MAP_DATE_RANGE_OPTIONS.LAST_7_DAYS;

/** Date range option labels for the dropdown (in display order). */
export const HEAT_MAP_DATE_RANGE_LABELS: {
  value: HeatMapDateRangeOption;
  label: string;
}[] = [
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.LAST_7_DAYS, label: "Last 7 Days" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.LAST_14_DAYS, label: "Last 14 Days" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.LAST_30_DAYS, label: "Last 30 Days" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.LAST_90_DAYS, label: "Last 90 Days" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.LAST_180_DAYS, label: "Last 180 Days" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.LAST_365_DAYS, label: "Last 365 Days" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.SPECIFIC_DATE, label: "Specific Date" },
  { value: HEAT_MAP_DATE_RANGE_OPTIONS.DATE_RANGE, label: "Date Range" },
];


/**
 * Heat Map metric definitions — single source of truth.
 *
 * `dateReliant: true` → the metric depends on analytics data for a given
 * date range (drives whether the date range picker is shown and whether
 * an API call is dispatched).
 */
export interface HeatMapMetricDef {
  label: string;
  value: string;
  dateReliant: boolean;
}

export const HEAT_MAP_METRICS: HeatMapMetricDef[] = [
  { label: "Inventory", value: "Inventory", dateReliant: false },
  { label: "Item Sales", value: "Item Sales", dateReliant: true },
  { label: "Items Sold", value: "Items Sold", dateReliant: true },
  { label: "Items Abandoned", value: "Items Abandoned", dateReliant: true },
  { label: "Abandonment Rate", value: "Abandonment Rate", dateReliant: true },
  { label: "Conversion Rate", value: "Conversion Rate", dateReliant: true },
  { label: "Product Views", value: "Product Views", dateReliant: true },
  { label: "Price", value: "Price", dateReliant: false },
  { label: "Stock Value", value: "Stock Value", dateReliant: false },
  { label: "SKU Coverage", value: "SKU Coverage", dateReliant: false },
  { label: "Sell Through", value: "Sell Through", dateReliant: true },
];

const ANALYTICS_DATE_FORMAT = "YYYYMMDD";

export interface ResolvedDateRange {
  startDate: string;
  endDate: string;
}

/**
 * Normalize an analytics date string to compact `YYYYMMDD` form so callers
 * can compare ranges regardless of which format produced them.
 * Accepts `YYYY-MM-DD` (Analytics overlay's format) or `YYYYMMDD`.
 */
export function normalizeAnalyticsDate(value: string | undefined): string {
  if (!value) return "";
  return value.replace(/-/g, "");
}

/**
 * Translate a predefined heat map date range option into actual start/end dates,
 */
export function resolvePredefinedDateRange(
  option: HeatMapDateRangeOption,
): ResolvedDateRange {
  const end = moment().subtract(1, "day");
  let start = moment(end);

  switch (option) {
    case HEAT_MAP_DATE_RANGE_OPTIONS.LAST_7_DAYS:
      start = moment().subtract(7, "days");
      break;
    case HEAT_MAP_DATE_RANGE_OPTIONS.LAST_14_DAYS:
      start = moment().subtract(14, "days");
      break;
    case HEAT_MAP_DATE_RANGE_OPTIONS.LAST_30_DAYS:
      start = moment().subtract(30, "days");
      break;
    case HEAT_MAP_DATE_RANGE_OPTIONS.LAST_90_DAYS:
      start = moment().subtract(90, "days");
      break;
    case HEAT_MAP_DATE_RANGE_OPTIONS.LAST_180_DAYS:
      start = moment().subtract(180, "days");
      break;
    case HEAT_MAP_DATE_RANGE_OPTIONS.LAST_365_DAYS:
      start = moment().subtract(365, "days");
      break;
    default:
      start = moment().subtract(7, "days");
      break;
  }

  return {
    startDate: start.format(ANALYTICS_DATE_FORMAT),
    endDate: end.format(ANALYTICS_DATE_FORMAT),
  };
}

/**
 * Resolve a date range from either a predefined option or explicit custom dates.
 * For SPECIFIC_DATE / DATE_RANGE, customStart/customEnd must be provided
 * (formatted as YYYYMMDD, MM/DD/YYYY, or any moment-parseable string).
 */
export function resolveDateRange(
  option: HeatMapDateRangeOption,
  customStart?: string,
  customEnd?: string,
): ResolvedDateRange {
  if (option === HEAT_MAP_DATE_RANGE_OPTIONS.SPECIFIC_DATE && customStart) {
    const d = moment(customStart).format(ANALYTICS_DATE_FORMAT);
    return { startDate: d, endDate: d };
  }
  if (option === HEAT_MAP_DATE_RANGE_OPTIONS.DATE_RANGE && customStart && customEnd) {
    return {
      startDate: moment(customStart).format(ANALYTICS_DATE_FORMAT),
      endDate: moment(customEnd).format(ANALYTICS_DATE_FORMAT),
    };
  }
  return resolvePredefinedDateRange(option);
}

/**
 * Build the heat map gradient (8 colors) from the chosen high and low.
 *
 * pick the slice of HEAT_MAP_SPECTRUM between the
 * high and low colors (inclusive). If high or low aren't on the spectrum,
 * return a clamped 8-color array using the chosen endpoints + interpolation.
 */
export function computeHeatMapGradient(
  highColor: string,
  lowColor: string,
): string[] {
  const highIndex = HEAT_MAP_SPECTRUM.indexOf(highColor);
  const lowIndex = HEAT_MAP_SPECTRUM.indexOf(lowColor);

  if (highIndex >= 0 && lowIndex >= 0 && highIndex <= lowIndex) {
    // Slice between the two ends (inclusive). Handles partial ranges.
    return HEAT_MAP_SPECTRUM.slice(highIndex, lowIndex + 1);
  }

  // Fallback: linear interpolation in RGB space between the two colors.
  return interpolateColors(highColor, lowColor, HEAT_MAP_GRADIENT_SIZE);
}

/** Linear RGB interpolation between two hex colors. */
export function interpolateColors(
  fromHex: string,
  toHex: string,
  steps: number,
): string[] {
  const from = hexToRgb(fromHex);
  const to = hexToRgb(toHex);
  if (!from || !to || steps < 1) return [fromHex];

  const colors: string[] = [];
  for (let i = 0; i < steps; i++) {
    const t = steps === 1 ? 0 : i / (steps - 1);
    const r = Math.round(from.r + (to.r - from.r) * t);
    const g = Math.round(from.g + (to.g - from.g) * t);
    const b = Math.round(from.b + (to.b - from.b) * t);
    colors.push(rgbToHex(r, g, b));
  }
  return colors;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return null;
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export interface ColorScale {
  /** Inclusive upper bound (highest value in this band) */
  upperBound: number;
  /** Exclusive lower bound (lowest value in this band) */
  lowerBound: number;
  /** Color for this band */
  color: string;
}

/**
 * Compute equal-interval color scales over the metric range.
 *
 * If all values are equal (max === min), returns N identical bands — one
 * per gradient color — where `interval = 0/N = 0`
 * caused every band to share the same upper/lower bound.
 *
 * Returns scales ordered from high → low (matching gradient[0..n]).
 */
export function computeColorScales(
  metricValues: number[],
  gradient: string[],
): ColorScale[] {
  if (!metricValues.length || !gradient.length) return [];

  const finiteValues = metricValues.filter((v) => Number.isFinite(v));
  if (!finiteValues.length) return [];

  const max = Math.max(...finiteValues);
  const min = Math.min(...finiteValues);

  if (max === min) {
    return gradient.map((color) => ({
      upperBound: max,
      lowerBound: min,
      color,
    }));
  }

  const numBands = gradient.length;
  const interval = (max - min) / numBands;
  const scales: ColorScale[] = [];

  // Highest band first.
  for (let i = 0; i < numBands; i++) {
    const upperBound = max - interval * i;
    const lowerBound = i === numBands - 1 ? min : max - interval * (i + 1);
    scales.push({
      upperBound,
      lowerBound,
      color: gradient[i],
    });
  }

  return scales;
}

/**
 * Assign each product a color based on its metric value falling into a scale band.
 * Returns a Map keyed by productId.
 *
 * Products with a missing or non-finite metric value get `undefined` (no color).
 */
export function assignProductColors<
  P extends { productId: string; [key: string]: any },
>(
  products: P[],
  metricKey: string,
  scales: ColorScale[],
): Map<string, string | undefined> {
  const result = new Map<string, string | undefined>();
  if (!scales.length) {
    products.forEach((p) => result.set(p.productId, undefined));
    return result;
  }

  products.forEach((product) => {
    const raw = product[metricKey];
    const value = typeof raw === "string" ? parseFloat(raw) : raw;

    if (!Number.isFinite(value)) {
      result.set(product.productId, undefined);
      return;
    }

    // Find the band this value falls into.
    // Bands are ordered high → low; pick the first one whose range contains the value.
    let assigned: string | undefined;
    for (let i = 0; i < scales.length; i++) {
      const { upperBound, lowerBound } = scales[i];
      if (value <= upperBound && value >= lowerBound) {
        assigned = scales[i].color;
        break;
      }
    }
    // Fallback if value sits at the absolute min (numerical edge case).
    if (assigned === undefined) {
      assigned = scales[scales.length - 1].color;
    }
    result.set(product.productId, assigned);
  });

  return result;
}

/**
 * Format a metric numeric value for tooltip display.
 * Keeps small floats with 2 decimals, integers as-is, big numbers with commas.
 */
export function formatHeatMapMetricValue(
  value: number | string | undefined,
): string {
  if (value === undefined || value === null || value === "") return "—";
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return String(value);
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toFixed(2);
}
